import { Button } from '@avanta/shared/components/ui/Button';
import { Card } from '@avanta/shared/components/ui/Card';
import { SelectController } from '@avanta/shared/components/ui/Select';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useUpdatePatient } from '@avanta/shared/hooks/useUpdatePatient';
import { i18nService } from '@avanta/shared/services/i18n';
import { type Patient } from '@avanta/shared/types/patients';
import { languageOptions } from '@avanta/shared/utils/common';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useUserStore } from 'stores/user';
import { type FormData } from 'types/form';

export const ChangeLanguage = (): React.ReactElement | null => {
  const { t } = useTranslation(LOCIZE_NS.PROFILE);
  const user = useUserStore(state => state.user);
  const { mutate: updatePatient } = useUpdatePatient();
  const {
    handleSubmit,
    control,
    watch,
    formState: { isSubmitting, isValid },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      language: user?.appLanguage,
    },
  });
  const isSubmitDisabled = !isValid || isSubmitting || user?.appLanguage === watch('language');

  const handleOnSubmit = async (data: FormData): Promise<void> => {
    try {
      updatePatient({
        ...user,
        appLanguage: data.language,
      } as Patient);
      i18nService.changeLanguage(String(data.language));
    } catch {
      toast.error(t('ChangeLanguage.error'));
    }
  };

  return (
    <Card className="bg-white" title={t('ChangeLanguage.heading')}>
      <form className="flex flex-col gap-6" onSubmit={handleSubmit(handleOnSubmit)}>
        <SelectController
          className="w-full am-lg:w-[calc(50%-14px)]"
          control={control}
          name="language"
          label={t('ChangeLanguage.form.language.label')}
          placeholder={t('ChangeLanguage.form.language.placeholder')}
          options={languageOptions}
        />
        <Button
          type="submit"
          className="w-full am-lg:w-fit min-w-45"
          disabled={isSubmitDisabled}
          isLoading={isSubmitting}
        >
          {t('ChangeLanguage.cta')}
        </Button>
      </form>
    </Card>
  );
};
